/**
 * PlantaDoSite — a unidade vista de cima, com um marcador por ativo.
 *
 * O que uma planta tem que a lista de ativos não tem é ADJACÊNCIA. Ver que as
 * três câmaras que estão subindo são as três do mesmo lado do salão muda o
 * diagnóstico: não são três problemas, é o compressor comum ou o sol batendo
 * naquela parede à tarde. Se a planta virar "a lista, só que espalhada", ela
 * não paga o próprio custo de manutenção.
 *
 * Decisões que sustentam isso:
 *
 *  • COORDENADA EM FRAÇÃO (0..1), nunca pixel. A imagem é desenhada em
 *    larguras diferentes em cada tela, e um dia alguém troca a planta por um
 *    scan maior. Em pixel, os dois casos espalham os marcadores.
 *
 *  • O MARCADOR É UM <button>. Ganha foco por teclado, tem rótulo acessível e
 *    navega pro ativo. Uma <div> com onClick não faz nada disso, e aqui o
 *    marcador é o principal caminho de navegação da tela.
 *
 *  • COR E FORMA JUNTAS. Cada estado tem ícone próprio além da cor — cor
 *    sozinha não sobrevive a daltonismo nem à impressão, e esta tela vai
 *    parar em relatório impresso.
 *
 *  • NENHUMA BIBLIOTECA. Uma <img> com botões posicionados em porcentagem
 *    resolve, e qualquer canvas pronto brigaria justamente no marcador, que é
 *    a superfície de design que importa aqui.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronDown, ImageOff, ImagePlus, MapPin, PencilRuler, Trash2, X,
} from "lucide-react";
import clsx from "clsx";

import toast from "react-hot-toast";

import { equipmentsApi, sitesApi } from "@/api";
import { apiErrorMessage } from "@/api/client";
import { avisarMudanca } from "@/lib/sincronia";
import {
  useActiveAlerts, useEquipmentsInSite, usePlantaDesenho, usePlantaImagem,
  useSites,
} from "@/hooks/queries";
import { useAuth } from "@/contexts/AuthContext";
import { tema as temaDaPlanta } from "@/lib/plantaDesenho";
import type { ActiveAlert, Equipment } from "@/types";

import {
  ESTADO, estadoDoAtivo, type Estado,
} from "@/lib/estadoDoAtivo";

import { Card, CardBody, CardHeader } from "./Card";
import { ConfirmDialog } from "./ConfirmDialog";
import { MenuDeAcoes } from "./MenuDeAcoes";
import { PreparadorDeImagem } from "./PreparadorDeImagem";
import { FormasDaPlanta } from "./FormasDaPlanta";
import { Skeleton } from "./Skeleton";
import { Button } from "./Button";

/** Aberto/fechado é preferência de pessoa, não de unidade — mesma chave em
 *  todas as plantas, como o resto dos colapsos do app. */
const CHAVE_ABERTA = "envpulse:planta-aberta";

function lerPreferencia(padrao: boolean): boolean {
  try {
    const v = localStorage.getItem(CHAVE_ABERTA);
    return v === null ? padrao : v === "1";
  } catch {
    return padrao;
  }
}

interface Props {
  siteId: string;
  /** Liga o modo de edição. Quem não é admin nunca recebe true. */
  editavel?: boolean;
  /** Mostra o controle de recolher. Sem isto o card é sempre aberto — que é
   *  o certo no kiosk, onde não há ninguém pra clicar. */
  colapsavel?: boolean;
  /** Estado inicial quando colapsável. */
  iniciaAberta?: boolean;
  /**
   * Faz o card caber na altura que recebe, em vez de crescer com a imagem.
   *
   * É o que o painel de parede precisa: lá a tela inteira tem que caber sem
   * rolagem, porque ninguém rola uma TV pendurada. Fora do painel o padrão
   * continua sendo crescer — numa página que rola, encolher a planta só
   * torna o desenho menor sem motivo.
   */
  ajustarNaAltura?: boolean;
  /**
   * Clicar num marcador abre o ativo.
   *
   * Falso no painel de parede: lá não há quem clique, e um toque acidental
   * tiraria a tela do painel sem ninguém por perto pra trazer de volta —
   * exatamente o que a regra "sem navegação" existe pra impedir. O
   * marcador continua informando; só deixa de ser porta.
   */
  navegavel?: boolean;
}

export function PlantaDoSite({
  siteId, editavel = false, colapsavel = false, iniciaAberta = true,
  ajustarNaAltura = false, navegavel = true,
}: Props) {
  const auth = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const sites = useSites();
  const site = sites.data?.find((s) => s.id === siteId);
  const ativos = useEquipmentsInSite(siteId);
  const alertas = useActiveAlerts();

  const [editando, setEditando] = useState(false);
  const [aberta, setAberta] = useState(() =>
    colapsavel ? lerPreferencia(iniciaAberta) : true);
  function alternarAberta() {
    setAberta((v) => {
      try { localStorage.setItem(CHAVE_ABERTA, v ? "0" : "1"); } catch { /* sem storage */ }
      return !v;
    });
  }
  const [pendente, setPendente] = useState<string | null>(null);
  /**
   * A imagem chegou mas o navegador não conseguiu desenhar.
   *
   * São duas falhas diferentes e eu só tratava a primeira: `imagem.erro` é o
   * download falhando (401, 404, rede). Esta é o download dando CERTO e o
   * `<img>` recusando o conteúdo — arquivo corrompido, tipo que o navegador
   * não decodifica. Sem este estado, a tela ficava com o ícone de imagem
   * quebrada e nenhuma explicação, que foi exatamente o que apareceu em
   * produção.
   */
  const [naoDesenhou, setNaoDesenhou] = useState(false);
  /** Arquivo escolhido, esperando enquadramento. */
  const [paraPreparar, setParaPreparar] = useState<File | null>(null);
  const imagemRef = useRef<HTMLImageElement>(null);
  const arquivoRef = useRef<HTMLInputElement>(null);

  /**
   * Altura disponível pro desenho, em pixels, medida do corpo do card.
   *
   * POR QUE MEDIDO E NÃO `max-h-full`: altura em porcentagem só resolve
   * contra um ancestral de altura DEFINIDA, e a cadeia até aqui passa por
   * caixas de altura automática — foi exatamente assim que as barras do
   * gráfico de energia sumiram no celular. Pixel medido não depende da
   * cadeia.
   *
   * O limite vai na `<img>`, não numa caixa em volta: o marcador é
   * posicionado em porcentagem da CAIXA DO ELEMENTO, então ela precisa ser
   * exatamente a imagem desenhada. Um `object-contain` dentro de uma caixa
   * maior criaria tarja preta e espalharia todo marcador.
   */
  const [caixaDisponivel, setCaixaDisponivel] =
    useState<{ largura: number; altura: number } | null>(null);
  const observador = useRef<ResizeObserver | null>(null);

  /**
   * REF DE CALLBACK, e não `useRef` + efeito, e isto era um bug de verdade.
   *
   * O nó medido só existe no ramo do desenho, e no primeiro render o ramo é
   * o esqueleto — os dados ainda estão vindo. Um efeito com dependências
   * `[ajustarNaAltura, aberta]` roda uma vez, encontra `null`, desiste, e
   * NUNCA volta a rodar quando o nó aparece. O observer não se ligava, a
   * altura ficava nula, e a imagem caía no caminho de fallback.
   *
   * A ref de callback é chamada pelo React exatamente quando o nó monta e
   * quando desmonta. Não tem como errar o momento.
   */
  const medirCaixa = useCallback((no: HTMLDivElement | null) => {
    observador.current?.disconnect();
    observador.current = null;
    if (!no || !ajustarNaAltura || typeof ResizeObserver === "undefined") {
      if (!ajustarNaAltura) setCaixaDisponivel(null);
      return;
    }
    const anotar = (l: number, a: number) => setCaixaDisponivel({ largura: l, altura: a });
    const obs = new ResizeObserver(([entrada]) => {
      anotar(entrada.contentRect.width, entrada.contentRect.height);
    });
    obs.observe(no);
    observador.current = obs;
    const r = no.getBoundingClientRect();
    anotar(r.width, r.height);
  }, [ajustarNaAltura]);

  useEffect(() => () => observador.current?.disconnect(), []);

  /**
   * O maior retângulo da proporção do desenho que cabe no espaço medido.
   *
   * CALCULADO, e não deixado pro CSS, depois de duas tentativas frustradas.
   * `aspect-ratio` + `max-width` + `max-height` parece resolver e não
   * resolve: com a largura explícita em 100%, o `max-height` CORTA a altura
   * sem devolver largura, e o quadro sai esticado — foi a planta "meio
   * torta", larga demais pro próprio desenho. Sem largura explícita, um
   * `<svg>` colapsa pros 300px default dele. Não existe, em CSS, "contenha
   * esta proporção nesta caixa" para um elemento qualquer.
   *
   * Com as duas dimensões medidas, é uma linha de aritmética, e o resultado
   * é exato — que é o que os marcadores exigem, já que são porcentagem
   * desta caixa.
   */
  function encaixeDoQuadro(largura: number, altura: number) {
    if (!caixaDisponivel || !largura || !altura) return undefined;
    const escala = Math.min(
      caixaDisponivel.largura / largura,
      caixaDisponivel.altura / altura,
    );
    if (!Number.isFinite(escala) || escala <= 0) return undefined;
    return {
      width: Math.floor(largura * escala),
      height: Math.floor(altura * escala),
    };
  }

  const podeEditar = editavel && auth.hasMinRole("admin");

  const porAtivo = useMemo(() => {
    const mapa = new Map<string, ActiveAlert[]>();
    for (const a of alertas.data ?? []) {
      const lista = mapa.get(a.equipment_id) ?? [];
      lista.push(a);
      mapa.set(a.equipment_id, lista);
    }
    return mapa;
  }, [alertas.data]);

  const vivos = (ativos.data ?? []).filter((e) => e.active);
  const posicionados = vivos.filter(
    (e) => e.planta_x !== null && e.planta_x !== undefined
         && e.planta_y !== null && e.planta_y !== undefined);
  const semPosicao = vivos.filter((e) => !posicionados.includes(e));

  const posicionarMut = useMutation({
    mutationFn: ({ id, x, y }: { id: string; x: number | null; y: number | null }) =>
      equipmentsApi.posicionar(id, { planta_x: x, planta_y: y }),
    // DEVOLVE a promessa, e isso não é estilo: o `mutateAsync` só resolve
    // depois que o `onSuccess` resolver. O marcador segura a posição local
    // até aqui, e sem esperar a recarga ele limparia o local com o dado
    // velho ainda no cache — um salto de volta pro lugar antigo, e depois
    // pro novo. `refetch` e não `invalidate` pelo mesmo motivo: invalidar
    // não garante que alguém vá recarregar.
    onSuccess: () => qc.refetchQueries({ queryKey: ["equipments"] }),
    // Falha silenciosa aqui é marcador que volta pro lugar antigo sem
    // explicação — e a pessoa tenta de novo achando que errou a mira.
    onError: (e) => toast.error(apiErrorMessage(e, "Não consegui salvar a posição")),
  });

  const uploadMut = useMutation({
    mutationFn: (file: File) => sitesApi.uploadPlanta(siteId, file),
    onSuccess: () => {
      setParaPreparar(null);
      toast.success("Planta importada");
      void qc.refetchQueries({ queryKey: ["sites"] });
      avisarMudanca([["sites"]]);
    },
    onError: (e) => toast.error(apiErrorMessage(e, "Não consegui enviar a imagem")),
  });

  /**
   * As duas remoções, e por que elas GRITAM quando falham.
   *
   * Antes não tinham tratamento de erro: a chamada ia, voltava 4xx, e a tela
   * ficava exatamente igual. O usuário lê isso como "o botão não funciona" e
   * clica de novo — que é o pior estado possível, porque a única informação
   * útil (o motivo) morreu no caminho.
   *
   * E `refetchQueries`, não `invalidateQueries`: invalidar só marca como
   * velho, e uma consulta sem observador ativo pode ficar marcada sem nunca
   * recarregar. Depois de apagar, a tela PRECISA refletir o estado novo — se
   * o desenho continua desenhado, ninguém acredita que apagou.
   */
  function apagou(mensagem: string) {
    setConfirmando(null);
    toast.success(mensagem);
    void qc.refetchQueries({ queryKey: ["sites"] });
    void qc.removeQueries({ queryKey: ["site", siteId, "desenho"] });
    avisarMudanca([["sites"], ["site", siteId, "desenho"]]);
  }

  function naoApagou(e: unknown) {
    setConfirmando(null);
    toast.error(apiErrorMessage(e, "Não consegui apagar"));
  }

  const removerMut = useMutation({
    mutationFn: () => sitesApi.removePlanta(siteId),
    onSuccess: () => apagou("Imagem removida"),
    onError: naoApagou,
  });

  const apagarDesenhoMut = useMutation({
    mutationFn: () => sitesApi.apagarDesenho(siteId),
    onSuccess: () => apagou("Desenho apagado"),
    onError: naoApagou,
  });

  /** Converte um ponto da tela em fração da imagem, grampeado em 0..1.
   *  O grampo não é paranoia: arrastar pra fora da borda é o gesto mais
   *  natural do mundo, e sem ele o banco recusa o valor e o marcador some. */
  function fracao(ev: { clientX: number; clientY: number }) {
    const caixa = imagemRef.current?.getBoundingClientRect();
    if (!caixa || !caixa.width || !caixa.height) return null;
    return {
      x: Math.min(1, Math.max(0, (ev.clientX - caixa.left) / caixa.width)),
      y: Math.min(1, Math.max(0, (ev.clientY - caixa.top) / caixa.height)),
    };
  }

  function soltarNaPlanta(ev: React.MouseEvent) {
    if (!editando || !pendente) return;
    const f = fracao(ev);
    if (!f) return;
    posicionarMut.mutate({ id: pendente, x: f.x, y: f.y });
    setPendente(null);
  }


  const temArquivo = !!site?.planta_path;

  // Só baixa com o card ABERTO e com arquivo cadastrado: recolhido, a planta
  // de uma unidade grande são megabytes que ninguém pediu; sem arquivo, é um
  // 404 garantido a cada abertura de tela.
  const imagem = usePlantaImagem(
    siteId, site?.planta_atualizada_em, aberta && temArquivo);

  const temDesenho = !!site?.planta_desenho_atualizado_em;
  const desenho = usePlantaDesenho(
    siteId, site?.planta_desenho_atualizado_em, aberta && temDesenho);
  /** Qual remoção está esperando confirmação. Apagar planta é raro e não tem
   *  desfazer — vale a pergunta. */
  const [confirmando, setConfirmando] = useState<"imagem" | "desenho" | null>(null);

  // Sem imagem não há onde posicionar. Deixar o modo de edição ligado
  // enquanto ela falha é como o bug nasceu: o marcador ia pro banco com
  // coordenada medida contra um retângulo vazio.
  useEffect(() => {
    if (imagem.erro || naoDesenhou) setEditando(false);
  }, [imagem.erro, naoDesenhou]);

  // Imagem nova zera o veredito da anterior — senão uma reimportação bem
  // sucedida continuaria mostrando o erro da tentativa que falhou.
  useEffect(() => { setNaoDesenhou(false); }, [imagem.url]);

  if (!site) return null;

  // Recurso desligado para este cliente (v40). O gate fica AQUI, e não em
  // cada tela, porque são quatro lugares que desenham planta e o quinto que
  // alguém escrever amanhã não vai lembrar de perguntar.
  if (site.plantas_habilitadas === false) return null;

  const falhou = imagem.erro || naoDesenhou;

  /**
   * O QUADRO é o que define o sistema de coordenadas dos marcadores: ou o
   * desenho, ou a imagem. Quando os dois existem o desenho manda, e a imagem
   * vira calco por baixo dele — senão haveria duas caixas de tamanhos
   * diferentes disputando a mesma fração 0..1, e todo marcador ficaria
   * errado por um deslocamento que ninguém consegue explicar.
   */
  const quadroDesenhado = temDesenho && !!desenho.data;
  const quadroFotografado = !temDesenho && temArquivo && !!imagem.url && !falhou;
  const desenhavel = quadroDesenhado || quadroFotografado;
  const temQuadro = temDesenho || temArquivo;

  /*
   * O arquivo escolhido NÃO sobe direto: passa pelo preparador.
   *
   * Planta de verdade vem grande e torta — scan A3 com margem, carimbo,
   * legenda e metade da folha em branco. Subindo inteiro, o desenho útil
   * ocupa um terço da tela da parede, e não havia o que fazer depois: a
   * imagem era imutável. Enquadrar antes é a única hora em que isso é
   * barato.
   */
  const entradaDeArquivo = (
    <input
      ref={arquivoRef} type="file" className="hidden"
      accept="image/png,image/jpeg,image/webp"
      onChange={(e) => {
        const f = e.target.files?.[0];
        if (f) setParaPreparar(f);
        e.target.value = "";
      }}
    />
  );

  const botaoRecolher = colapsavel && (
    <button
      type="button"
      onClick={alternarAberta}
      aria-expanded={aberta}
      className="inline-flex items-center gap-1 rounded-md border border-surface-300 px-2.5 py-1 text-sm font-medium text-slate-600 hover:bg-surface-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
    >
      <ChevronDown
        className={clsx("h-4 w-4 transition-transform", aberta && "rotate-180")}
        aria-hidden
      />
      {aberta ? "Recolher" : "Planta"}
    </button>
  );

  return (
    <Card className={clsx(ajustarNaAltura && "flex h-full flex-col overflow-hidden")}>
      {/* O cabeçalho é UM só pros três estados (sem planta, carregando,
          desenhada). Antes o estado vazio tinha cabeçalho próprio, e com ele
          o botão de recolher sumia justamente na unidade que mais ocupa tela
          à toa — a que não usa planta nenhuma. */}
      <CardHeader
        title="Planta da unidade"
        subtitle={site.name}
        actions={
          (podeEditar || colapsavel) && (
            <div className="flex items-center gap-2">
              {botaoRecolher}
              {/* Aba nova: desenhar é trabalho longo e precisa de tela
                  inteira. Dentro deste card a planta dividia a largura com o
                  resto da página e a altura com tudo o que vem depois — era
                  desenhar por um buraco. */}
              {podeEditar && (
                <Link
                  to={`/sites/${siteId}/planta`}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-1.5 rounded-md border border-surface-300 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-surface-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <PencilRuler className="h-4 w-4" aria-hidden />
                  {temDesenho ? "Editar planta" : "Desenhar"}
                </Link>
              )}
              {podeEditar && desenhavel && (
                <Button
                  variant={editando ? "primary" : "secondary"}
                  onClick={() => { setEditando((v) => !v); setPendente(null); }}
                >
                  {editando ? "Concluir" : "Posicionar"}
                </Button>
              )}
              {/* O raro e o destrutivo ficam no menu, não ao lado do que se
                  usa toda semana. Antes "remover imagem" vivia atrás do modo
                  de posicionar, o que na prática a tornava inalcançável:
                  quem queria trocar a planta não tinha por onde. */}
              {podeEditar && (
                <>
                  {entradaDeArquivo}
                  <MenuDeAcoes
                    acoes={[
                      {
                        rotulo: temArquivo ? "Trocar imagem" : "Importar imagem",
                        Icone: ImagePlus,
                        onClick: () => arquivoRef.current?.click(),
                      },
                      temArquivo && {
                        rotulo: "Remover imagem",
                        Icone: ImageOff,
                        destrutiva: true,
                        onClick: () => setConfirmando("imagem"),
                      },
                      temDesenho && {
                        rotulo: "Apagar desenho",
                        Icone: Trash2,
                        destrutiva: true,
                        onClick: () => setConfirmando("desenho"),
                      },
                    ]}
                  />
                </>
              )}
            </div>
          )
        }
      />

      {aberta && (
        <CardBody className={clsx(ajustarNaAltura && "flex min-h-0 flex-1 flex-col")}>
          {!temQuadro ? (
            // ---- unidade sem planta ----------------------------------------
            <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-surface-300 p-8 text-center dark:border-slate-700">
              <MapPin className="h-8 w-8 text-slate-400" aria-hidden />
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Esta unidade ainda não tem planta cadastrada.
              </p>
              {podeEditar && (
                <>
                  {entradaDeArquivo}
                  {/* Duas portas de entrada, e desenhar vem primeiro: exigir
                      um arquivo pronto exclui quem não tem, que é a maior
                      parte — a planta da operação costuma ser uma folha na
                      parede ou nada. */}
                  <div className="flex flex-wrap justify-center gap-2">
                    <Link
                      to={`/sites/${siteId}/planta`}
                      target="_blank"
                      rel="noopener"
                      className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600"
                    >
                      <PencilRuler className="h-4 w-4" aria-hidden />
                      Desenhar a planta
                    </Link>
                    <Button variant="secondary" onClick={() => arquivoRef.current?.click()}
                            loading={uploadMut.isPending}>
                      <ImagePlus className="mr-1.5 h-4 w-4" aria-hidden />
                      Importar imagem
                    </Button>
                  </div>
                  {uploadMut.isError && (
                    <p className="text-xs text-rose-600 dark:text-rose-400">
                      Não consegui enviar. Use PNG, JPG ou WebP com até 8 MB.
                    </p>
                  )}
                </>
              )}
            </div>
          ) : !temDesenho && falhou ? (
            // ---- o registro existe, a imagem não veio ----------------------
            // Este estado precisa existir escrito. Sem ele o navegador mostra
            // o ícone de imagem quebrada, que o usuário lê como "o upload
            // falhou" — e o upload funcionou.
            <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-rose-300 p-8 text-center dark:border-rose-900">
              <ImageOff className="h-8 w-8 text-rose-400" aria-hidden />
              <div>
                <p className="text-sm font-medium text-rose-700 dark:text-rose-300">
                  Não consegui carregar a imagem desta planta.
                </p>
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                  {naoDesenhou
                    ? "O arquivo chegou, mas o navegador não conseguiu abri-lo — pode estar corrompido ou ter sido salvo num formato diferente da extensão."
                    : "O cadastro da planta existe, mas o arquivo não voltou do servidor."}
                  {" "}
                  Importar de novo resolve; as posições dos ativos são
                  preservadas.
                </p>
              </div>
              {podeEditar && (
                <>
                  {entradaDeArquivo}
                  <Button onClick={() => arquivoRef.current?.click()}
                          loading={uploadMut.isPending}>
                    <ImagePlus className="mr-1.5 h-4 w-4" aria-hidden />
                    Importar imagem
                  </Button>
                </>
              )}
            </div>
          ) : !desenhavel ? (
            <Skeleton className="h-64 w-full rounded-lg" />
          ) : (
            // ---- a planta desenhada ----------------------------------------
            <>
              {/* O aviso de cadastro incompleto é a defesa contra a planta que
                  envelhece mentindo. Ativo novo entra aqui sozinho, visível — e
                  não silenciosamente ausente do desenho, que é como uma planta
                  passa a afirmar que a unidade tem menos equipamentos do que
                  tem. */}
              {semPosicao.length > 0 && (
                <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-800 dark:bg-amber-950/40">
                  <span className="font-medium text-amber-900 dark:text-amber-200">
                    {semPosicao.length}{" "}
                    {semPosicao.length === 1 ? "ativo ainda não posicionado" : "ativos ainda não posicionados"}
                  </span>
                  {podeEditar && !editando && (
                    <button
                      type="button"
                      className="ml-2 underline underline-offset-2 text-amber-900 dark:text-amber-200"
                      onClick={() => setEditando(true)}
                    >
                      posicionar agora
                    </button>
                  )}
                  {editando && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {semPosicao.map((e) => (
                        <button
                          key={e.id}
                          type="button"
                          onClick={() => setPendente(pendente === e.id ? null : e.id)}
                          className={clsx(
                            "rounded-full border px-2.5 py-1 text-xs",
                            pendente === e.id
                              ? "border-brand-500 bg-brand-500 text-white"
                              : "border-amber-400 text-amber-900 dark:text-amber-200",
                          )}
                        >
                          {e.name}
                        </button>
                      ))}
                      <span className="self-center text-xs text-amber-800 dark:text-amber-300">
                        {pendente ? "agora clique no lugar dele na planta" : "escolha um e clique na planta"}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* O modo de posicionar precisa dizer o que dá pra fazer
                  nele. Arrastar é descobrível; tirar da planta não era, e
                  passou a ter botão — mas continua valendo escrever, porque
                  quem entra aqui costuma entrar uma vez por semestre. */}
              {editando && (
                <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
                  Arraste os marcadores para reposicionar. O ✕ tira o ativo da
                  planta — ele continua monitorado, só deixa de aparecer aqui.
                </p>
              )}

              {/* A área medida é esta, e não o corpo inteiro: o aviso de
                  ativo não posicionado e a legenda também ocupam altura, e
                  medir o corpo daria à imagem um teto maior que o espaço
                  que ela realmente tem. */}
              <div
                ref={medirCaixa}
                className={clsx(
                  ajustarNaAltura && "flex min-h-0 flex-1 items-center justify-center",
                )}
              >
              {/*
                A CAIXA DO QUADRO. Ela precisa ser EXATAMENTE o desenho, nem
                um pixel a mais: os marcadores são porcentagem dela, e
                qualquer sobra vira deslocamento de todos eles.

                Os dois caminhos chegam nisso por vias opostas, porque os
                elementos são diferentes:

                • IMAGEM tem tamanho natural. `w-fit` encolhe a caixa até
                  ela, e o teto de altura medido faz a imagem caber.

                • DESENHO não tem. `w-fit` em volta de um `<svg>` com
                  largura percentual colapsa para os 300px de tamanho padrão
                  do SVG — pequeno e centralizado no meio da coluna, que foi
                  o que apareceu no painel. Aqui a caixa é quem manda:
                  recebe a proporção do documento e os dois tetos, e o
                  navegador resolve o maior retângulo daquela proporção que
                  cabe. O `<svg>` dentro só preenche.
              */}
              <div
                className={clsx(
                  "relative select-none",
                  ajustarNaAltura && (quadroDesenhado ? "m-auto" : "mx-auto w-fit"),
                  editando && pendente && "cursor-crosshair",
                )}
                style={
                  ajustarNaAltura && quadroDesenhado && desenho.data
                    ? encaixeDoQuadro(desenho.data.largura, desenho.data.altura)
                    : undefined
                }
                onClick={soltarNaPlanta}
              >
                {quadroDesenhado && desenho.data ? (
                  /*
                   * A LARGURA DO SVG É EXPLÍCITA, e isto é o oposto do que
                   * funciona pra `<img>`.
                   *
                   * `<img>` tem dimensão intrínseca — o arquivo sabe quanto
                   * mede —, então `width:auto` + `max-height` basta: o
                   * navegador encolhe mantendo a proporção. `<svg>` NÃO tem:
                   * é elemento substituído sem tamanho natural, e com
                   * `width:auto` ele não tem de onde tirar medida. Colapsa,
                   * a caixa `w-fit` em volta colapsa junto, e aí os
                   * marcadores — que são porcentagem dessa caixa — caem
                   * todos empilhados no mesmo ponto. Foi exatamente o que
                   * apareceu no modo painel: desenho invisível e ativos um
                   * em cima do outro.
                   *
                   * Com largura em pixel e `aspect-ratio`, a altura é
                   * derivada e nunca passa do espaço medido. `maxWidth`
                   * cobre a outra ponta: quadro largo demais encolhe pela
                   * largura e sobra altura, em vez de estourar.
                   */
                  <svg
                    ref={imagemRef as unknown as React.RefObject<SVGSVGElement>}
                    viewBox={`0 0 ${desenho.data.largura} ${desenho.data.altura}`}
                    className={clsx(
                      "block rounded-lg border border-surface-200 dark:border-slate-700",
                      ajustarNaAltura ? "h-full w-full" : "w-full",
                    )}
                    style={{
                      // O papel vem do DESENHO, não do tema do app: a planta
                      // pode viver escura na TV do salão e clara no
                      // relatório do mesmo cliente.
                      background: temaDaPlanta(desenho.data.tema).papel,
                      // Fora do modo painel não há teto de altura, então a
                      // proporção é o que dá altura ao SVG — sem ela, os
                      // 150px default.
                      ...(ajustarNaAltura
                        ? {}
                        : { aspectRatio: `${desenho.data.largura} / ${desenho.data.altura}` }),
                    }}
                    role="img"
                    aria-label={`Planta da unidade ${site.name}`}
                  >
                    {/* A imagem importada, quando existe, fica por baixo do
                        traço: quem desenhou por cima de um scan continua
                        vendo o scan. */}
                    {imagem.url && (
                      <image
                        href={imagem.url} x={0} y={0}
                        width={desenho.data.largura} height={desenho.data.altura}
                        preserveAspectRatio="xMidYMid meet" opacity={0.45}
                      />
                    )}
                    <FormasDaPlanta formas={desenho.data.formas} tema={desenho.data.tema} />
                  </svg>
                ) : (
                  <img
                    ref={imagemRef}
                    src={imagem.url ?? undefined}
                    alt={`Planta da unidade ${site.name}`}
                    className={clsx(
                      "rounded-lg border border-surface-200 dark:border-slate-700",
                      ajustarNaAltura ? "h-auto w-auto max-w-full" : "w-full",
                    )}
                    style={
                      ajustarNaAltura && caixaDisponivel
                        ? { maxHeight: caixaDisponivel.altura,
                            maxWidth: caixaDisponivel.largura }
                        : undefined
                    }
                    draggable={false}
                    // Sem isto, imagem que não desenha vira ícone quebrado
                    // com marcadores posicionáveis por cima — e o marcador
                    // vai pro banco com coordenada medida contra um
                    // retângulo vazio.
                    onError={() => setNaoDesenhou(true)}
                    onLoad={() => setNaoDesenhou(false)}
                  />
                )}

                {posicionados.map((eq) => (
                  <Marcador
                    key={eq.id}
                    ativo={eq}
                    estado={estadoDoAtivo(porAtivo.get(eq.id) ?? [])}
                    editando={editando}
                    onAbrir={navegavel
                      ? () => navigate(`/equipments/${eq.id}`)
                      : undefined}
                    onMover={(x, y) => posicionarMut.mutateAsync({ id: eq.id, x, y })}
                    onTirar={() => posicionarMut.mutate({ id: eq.id, x: null, y: null })}
                    fracao={fracao}
                  />
                ))}
              </div>
              </div>

              <Legenda />
            </>
          )}
        </CardBody>
      )}

      {/* As duas frases dizem o que SOBREVIVE, não só o que some. É o que
          faz a pessoa clicar sem medo — e o medo aqui custa caro: quem não
          remove a imagem errada convive com ela. */}
      {paraPreparar && (
        <PreparadorDeImagem
          arquivo={paraPreparar}
          enviando={uploadMut.isPending}
          aoCancelar={() => setParaPreparar(null)}
          aoConfirmar={(imagem) => uploadMut.mutate(imagem)}
        />
      )}

      <ConfirmDialog
        open={confirmando === "imagem"}
        title="Remover a imagem?"
        message={
          temDesenho
            ? "A imagem de fundo sai. O desenho e as posições dos ativos continuam como estão."
            : "A imagem sai e a unidade fica sem planta. As posições dos ativos são preservadas — importar outra imagem traz todos de volta."
        }
        confirmLabel="Remover"
        loading={removerMut.isPending}
        onConfirm={() => removerMut.mutate()}
        onCancel={() => setConfirmando(null)}
      />
      <ConfirmDialog
        open={confirmando === "desenho"}
        title="Apagar o desenho?"
        message={
          temArquivo
            ? "Todas as formas desenhadas são apagadas. A imagem importada e as posições dos ativos continuam."
            : "Todas as formas desenhadas são apagadas e a unidade fica sem planta. As posições dos ativos são preservadas."
        }
        confirmLabel="Apagar"
        loading={apagarDesenhoMut.isPending}
        onConfirm={() => apagarDesenhoMut.mutate()}
        onCancel={() => setConfirmando(null)}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------

/**
 * O marcador de um ativo sobre a planta.
 *
 * É o principal caminho de navegação desta tela, daí ser um `<button>`: ganha
 * foco por teclado e anuncia rótulo, coisas que uma `<div>` com onClick não
 * faz.
 *
 * TIRAR DA PLANTA PRECISA DE BOTÃO, e isto já custou uma correção: existia só
 * pela tecla Delete sobre o marcador focado. Ninguém descobre, e num tablet
 * no galpão — que é onde esta tela é usada — não há teclado nenhum. Ação que
 * existe e não se alcança é pior que ação que não existe: a primeira faz a
 * pessoa procurar.
 *
 * O ✕ é irmão do marcador, não filho: `<button>` dentro de `<button>` é HTML
 * inválido, e o navegador desfaz o aninhamento de um jeito que come o clique.
 */
function Marcador({
  ativo, estado, editando, onAbrir, onMover, onTirar, fracao,
}: {
  ativo: Equipment;
  estado: Estado;
  editando: boolean;
  onAbrir?: () => void;
  onMover: (x: number, y: number) => Promise<unknown>;
  onTirar: () => void;
  fracao: (ev: { clientX: number; clientY: number }) => { x: number; y: number } | null;
}) {
  const { rotulo, cor, Icone } = ESTADO[estado];
  const [arrastando, setArrastando] = useState(false);

  /**
   * Onde o marcador está ENQUANTO o dedo/mouse arrasta.
   *
   * Antes o arrasto gravava a cada `pointermove`: dezenas de PATCH por
   * gesto, o marcador só andava quando a resposta voltava, e as respostas
   * chegando fora de ordem o puxavam pra trás. Na prática, arrastar com o
   * mouse não funcionava — pelo teclado, que é um evento por toque,
   * funcionava.
   *
   * Agora o arrasto é local e instantâneo, e grava UMA vez ao soltar. O
   * local é limpo quando a gravação termina, dê certo ou errado: se der
   * errado, o marcador volta pra verdade do servidor em vez de mostrar uma
   * posição que ninguém guardou.
   */
  const [local, setLocal] = useState<{ x: number; y: number } | null>(null);

  const x = local?.x ?? ativo.planta_x ?? 0;
  const y = local?.y ?? ativo.planta_y ?? 0;

  async function soltar() {
    setArrastando(false);
    if (!local) return;
    try {
      await onMover(local.x, local.y);
    } finally {
      setLocal(null);
    }
  }

  return (
    <div
      className={clsx("absolute -translate-x-1/2 -translate-y-1/2", arrastando && "z-20")}
      style={{ left: `${x * 100}%`, top: `${y * 100}%` }}
    >
      <button
        type="button"
        // `touch-none` porque sem ele o navegador interpreta o arrasto como
        // rolagem da página e o marcador não sai do lugar no celular.
        className={clsx(
          "block rounded-full",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400",
            editando ? "cursor-grab touch-none active:cursor-grabbing"
          : onAbrir ? "cursor-pointer" : "cursor-default",
        )}
        aria-label={`${ativo.name} — ${rotulo}`}
        title={editando ? `${ativo.name} — arraste para reposicionar` : `${ativo.name} — ${rotulo}`}
        onClick={(e) => { e.stopPropagation(); if (!editando && !arrastando) onAbrir?.(); }}
        onPointerDown={(e) => {
          if (!editando) return;
          e.stopPropagation();
          // Captura no PRÓPRIO botão, não no `e.target`: o alvo pode ser o
          // <svg> do ícone lá dentro, e capturar num filho deixa o gesto
          // dependendo de o evento borbulhar. E dentro de try: navegador que
          // recuse a captura não pode impedir o arrasto de começar.
          try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* segue */ }
          setArrastando(true);
        }}
        onPointerMove={(e) => {
          if (!arrastando) return;
          const f = fracao(e);
          if (f) setLocal(f);
        }}
        onPointerUp={() => { void soltar(); }}
        onPointerCancel={() => { void soltar(); }}
        // Teclado: quem navega por tab não consegue arrastar. As setas movem
        // 1% por toque, que é o suficiente pra ajustar sem mouse.
        onKeyDown={(e) => {
          if (!editando) return;
          const passo = 0.01;
          const mapa: Record<string, [number, number]> = {
            ArrowLeft: [x - passo, y], ArrowRight: [x + passo, y],
            ArrowUp: [x, y - passo],   ArrowDown: [x, y + passo],
          };
          if (mapa[e.key]) {
            e.preventDefault();
            const [nx, ny] = mapa[e.key];
            void onMover(Math.min(1, Math.max(0, nx)), Math.min(1, Math.max(0, ny)));
          }
          if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); onTirar(); }
        }}
      >
        <span
          className="flex h-7 w-7 items-center justify-center rounded-full text-white shadow-md ring-2 ring-white dark:ring-slate-900"
          style={{ background: cor }}
        >
          <Icone className="h-4 w-4" aria-hidden />
        </span>
      </button>

      {editando && !arrastando && (
        <button
          type="button"
          // Alvo de 20px num marcador de 28px: menor que isso não se acerta
          // com o dedo, maior cobre o próprio marcador.
          className="absolute -right-2.5 -top-2.5 flex h-5 w-5 items-center justify-center rounded-full border border-white bg-slate-700 text-white shadow hover:bg-rose-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 dark:border-slate-900"
          aria-label={`Tirar ${ativo.name} da planta`}
          title="Tirar da planta"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onTirar(); }}
        >
          <X className="h-3 w-3" aria-hidden />
        </button>
      )}

      <span className="pointer-events-none absolute left-1/2 top-full mt-1 -translate-x-1/2 whitespace-nowrap rounded bg-white/90 px-1.5 py-0.5 text-[11px] font-medium text-slate-700 shadow-sm dark:bg-slate-900/90 dark:text-slate-200">
        {ativo.name}
      </span>
    </div>
  );
}

function Legenda() {
  return (
    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
      {(Object.keys(ESTADO) as Estado[]).map((k) => {
        const { rotulo, cor, Icone } = ESTADO[k];
        return (
          <span key={k} className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
            <span className="flex h-4 w-4 items-center justify-center rounded-full text-white"
                  style={{ background: cor }}>
              <Icone className="h-2.5 w-2.5" aria-hidden />
            </span>
            {rotulo}
          </span>
        );
      })}
    </div>
  );
}
